/**
 * Battlefield post-processing. One WebGL pass on the main camera that applies
 * saturation, contrast, a per-biome color grade, a vignette and a whisper of
 * film grain — the difference between "tiles on a screen" and a graded shot.
 *
 * Why a shader instead of stacked overlay sprites: a single fullscreen pass
 * costs one draw call and lets the grade multiply the *rendered* frame, so
 * towers, enemies and the painted background all sit in the same light. Layered
 * alpha rectangles would wash out highlights and cost fill rate per layer.
 *
 * Canvas fallback: when the renderer isn't WebGL every method is a no-op and
 * the game renders ungraded rather than breaking.
 *
 * Lifecycle: GameScene calls postFx.attach(this, biome) in create() and
 * postFx.update(delta) from its update loop (real delta, not speed-scaled —
 * grain and pulses are presentation, not simulation).
 */
import Phaser from "phaser";
import { Biome } from "../maps/maps";

const PIPELINE_KEY = "BattlefieldGrade";

const FRAG = `
precision mediump float;

uniform sampler2D uMainSampler;
uniform vec2  uResolution;
uniform vec3  uTint;
uniform float uSaturation;
uniform float uContrast;
uniform float uVignette;
uniform float uVigInner;
uniform float uVigOuter;
uniform float uGrain;
uniform float uTime;
/** Transient combat flash: rgb tint added, w = strength. */
uniform vec4  uPulse;

varying vec2 outTexCoord;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main() {
  vec4 src = texture2D(uMainSampler, outTexCoord);
  vec3 c = src.rgb;

  // Saturation, pivoted on perceptual luma.
  float luma = dot(c, vec3(0.299, 0.587, 0.114));
  c = mix(vec3(luma), c, uSaturation);

  // Contrast around mid grey.
  c = (c - 0.5) * uContrast + 0.5;

  // Biome grade.
  c *= uTint;

  // Transient combat pulse (boss kill, castle hit) tints the whole frame.
  c = mix(c, uPulse.rgb, clamp(uPulse.w, 0.0, 1.0));

  // Vignette. Aspect-corrected so it stays circular on a 16:9 canvas.
  vec2 uv = outTexCoord - 0.5;
  uv.x *= uResolution.x / max(uResolution.y, 1.0);
  float d = length(uv);
  float falloff = 1.0 - smoothstep(uVigInner, uVigOuter, d);
  c *= 1.0 - uVignette * (1.0 - falloff);

  // Film grain — keeps flat painted areas from banding.
  if (uGrain > 0.0) {
    float g = hash(outTexCoord * uResolution + uTime) - 0.5;
    c += g * uGrain;
  }

  gl_FragColor = vec4(clamp(c, 0.0, 1.0), src.a);
}
`;

export interface Grade {
  /** Per-channel multiply. 1,1,1 = neutral. */
  tint: [number, number, number];
  saturation: number;
  contrast: number;
  /** 0 = off, 1 = corners fully black. */
  vignette: number;
  vigInner: number;
  vigOuter: number;
  grain: number;
}

const NEUTRAL: Grade = {
  tint: [1, 1, 1], saturation: 1, contrast: 1,
  vignette: 0.3, vigInner: 0.42, vigOuter: 0.92, grain: 0.02
};

/**
 * Per-biome look.
 *
 * Deliberately restrained. The painted backgrounds already carry almost all of
 * the colour, so the grade's job is to *seat* everything in one light, not to
 * repaint the art. An earlier, much heavier pass (tints up to ±17%, vignette
 * 0.52, inner radius 0.30) washed the plains map to sepia and darkened the map
 * corners by ~40% — and in a tower defense the corners are playable space you
 * place towers on, so that cost readability, not just taste.
 *
 * Rules of thumb held below: tint within ±4% per channel, contrast under 1.06,
 * and vigInner >= 0.58 so the falloff only ever bites the true corners.
 */
const BIOME_GRADE: Record<Biome, Grade> = {
  // Warm late-afternoon sun over farmland.
  plains: { tint: [1.02, 1.00, 0.975], saturation: 1.02, contrast: 1.02, vignette: 0.17, vigInner: 0.64, vigOuter: 1.02, grain: 0.014 },
  // Cool, damp, slightly hazy.
  river:  { tint: [0.985, 1.00, 1.025], saturation: 1.01, contrast: 1.02, vignette: 0.16, vigInner: 0.66, vigOuter: 1.03, grain: 0.013 },
  // Sickly and low-sun, but still readable.
  swamp:  { tint: [0.975, 1.01, 0.965], saturation: 0.97, contrast: 1.04, vignette: 0.24, vigInner: 0.60, vigOuter: 1.00, grain: 0.020 },
  // Overcast snow: lightly desaturated and blue-shifted.
  snow:   { tint: [0.99, 1.00, 1.035], saturation: 0.96, contrast: 1.01, vignette: 0.14, vigInner: 0.68, vigOuter: 1.04, grain: 0.012 },
  // Underground amber torchlight — the darkest look we allow.
  mine:   { tint: [1.03, 0.99, 0.945], saturation: 0.99, contrast: 1.05, vignette: 0.28, vigInner: 0.58, vigOuter: 0.99, grain: 0.022 },
  // Bleached midday heat.
  desert: { tint: [1.035, 1.00, 0.955], saturation: 1.02, contrast: 1.02, vignette: 0.18, vigInner: 0.64, vigOuter: 1.02, grain: 0.015 },
  // Ash and smoke over a burning siege line.
  siege:  { tint: [1.025, 0.99, 0.965], saturation: 0.95, contrast: 1.04, vignette: 0.26, vigInner: 0.60, vigOuter: 1.00, grain: 0.021 }
};

export class BattlefieldGradePipeline extends Phaser.Renderer.WebGL.Pipelines.PostFXPipeline {
  grade: Grade = NEUTRAL;
  /** Seconds since attach, drives grain. */
  timeSec = 0;
  /** Transient full-frame tint: [r, g, b, strength]. */
  pulse: [number, number, number, number] = [1, 1, 1, 0];

  constructor(game: Phaser.Game) {
    super({ game, name: PIPELINE_KEY, fragShader: FRAG });
  }

  onPreRender(): void {
    const g = this.grade;
    this.set2f("uResolution", this.renderer.width, this.renderer.height);
    this.set3f("uTint", g.tint[0], g.tint[1], g.tint[2]);
    this.set1f("uSaturation", g.saturation);
    this.set1f("uContrast", g.contrast);
    this.set1f("uVignette", g.vignette);
    this.set1f("uVigInner", g.vigInner);
    this.set1f("uVigOuter", g.vigOuter);
    this.set1f("uGrain", g.grain);
    this.set1f("uTime", this.timeSec);
    this.set4f("uPulse", this.pulse[0], this.pulse[1], this.pulse[2], this.pulse[3]);
  }
}

class PostFxController {
  private pipe?: BattlefieldGradePipeline;
  private camera?: Phaser.Cameras.Scene2D.Camera;
  private registered = false;
  /** Remaining pulse time in ms and its peak strength. */
  private pulseMs = 0;
  private pulseMaxMs = 1;
  private pulsePeak = 0;

  /** True when the grade is live (WebGL only). */
  get enabled(): boolean { return !!this.pipe; }

  /**
   * Attach the grade to a scene's main camera. Safe to call on every
   * GameScene.create(); re-attaching replaces the previous binding.
   */
  attach(scene: Phaser.Scene, biome: Biome): void {
    this.detach();
    const renderer = scene.game.renderer;
    if (renderer.type !== Phaser.WEBGL) return;
    const webgl = renderer as Phaser.Renderer.WebGL.WebGLRenderer;

    if (!this.registered) {
      webgl.pipelines.addPostPipeline(PIPELINE_KEY, BattlefieldGradePipeline);
      this.registered = true;
    }

    const cam = scene.cameras.main;
    cam.setPostPipeline(BattlefieldGradePipeline);
    const pipe = cam.getPostPipeline(BattlefieldGradePipeline);
    // getPostPipeline returns an array when several instances match.
    const inst = Array.isArray(pipe) ? pipe[0] : pipe;
    if (!(inst instanceof BattlefieldGradePipeline)) return;

    this.pipe = inst;
    this.camera = cam;
    this.pipe.grade = BIOME_GRADE[biome] ?? NEUTRAL;
    this.pipe.timeSec = 0;
    this.pipe.pulse = [1, 1, 1, 0];
    this.pulseMs = 0;

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.detach());
  }

  detach(): void {
    if (this.camera) {
      try { this.camera.resetPostPipeline(); } catch { /* camera already torn down */ }
    }
    this.camera = undefined;
    this.pipe = undefined;
    this.pulseMs = 0;
  }

  /** Advance grain + decay any active pulse. Pass the REAL delta, not scaled. */
  update(deltaMs: number): void {
    const pipe = this.pipe;
    if (!pipe) return;
    pipe.timeSec += deltaMs / 1000;
    if (this.pulseMs > 0) {
      this.pulseMs -= deltaMs;
      if (this.pulseMs <= 0) {
        this.pulseMs = 0;
        pipe.pulse[3] = 0;
      } else {
        // Ease out so the flash snaps in and drains away.
        const k = this.pulseMs / this.pulseMaxMs;
        pipe.pulse[3] = this.pulsePeak * k * k;
      }
    }
  }

  /**
   * Flash the whole frame toward a color. Used for castle hits (red), boss
   * kills (gold) and wave clears. `strength` is 0..1 at its peak.
   */
  flash(r: number, g: number, b: number, strength: number, durationMs: number): void {
    const pipe = this.pipe;
    if (!pipe) return;
    pipe.pulse[0] = r;
    pipe.pulse[1] = g;
    pipe.pulse[2] = b;
    this.pulsePeak = strength;
    this.pulseMs = durationMs;
    this.pulseMaxMs = durationMs;
    pipe.pulse[3] = strength;
  }

  /** Override the active grade — used by the day/night cycle. */
  setGrade(grade: Grade): void {
    if (this.pipe) this.pipe.grade = grade;
  }

  /** The unmodified preset for a biome, for callers that want to blend from it. */
  gradeFor(biome: Biome): Grade {
    return BIOME_GRADE[biome] ?? NEUTRAL;
  }
}

export const postFx = new PostFxController();
